import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StepNav, { DRUGCALC_STEPS } from "@/components/StepNav";
import { FurtherReading } from "@/components/FurtherReading";
import { BasicCalculator } from "@/components/drugcalc/BasicCalculator";
import { WorksheetRow } from "@/components/drugcalc/WorksheetRow";
import { SaveSession } from "@/components/SaveSession";
import { PREMED_DRUGS, INDUCTION_DRUGS, calcVolumeForDose } from "@/data/drugs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Calculator,
  Syringe,
  RotateCcw,
  ChevronLeft,
  Eye,
  EyeOff,
  Dices,
  Info,
} from "lucide-react";
import { toast } from "sonner";

// Injectable drugs only (volatiles excluded — no volume to draw up).
const DRUG_POOL = [...PREMED_DRUGS, ...INDUCTION_DRUGS];
const byId = (id) => DRUG_POOL.find((d) => d.id === id);

const roundDose = (v) => {
  if (v >= 10) return Math.round(v);
  if (v >= 1) return Math.round(v * 10) / 10;
  if (v >= 0.1) return Math.round(v * 100) / 100;
  return Math.round(v * 1000) / 1000;
};

// Pick a clinically sensible ordered dose within the drug's range.
const pickDose = (drug, species) => {
  const range = species === "cat" ? drug.doseCat : drug.doseDog;
  const pts = [range.low, (range.low + range.high) / 2, range.high];
  const raw = pts[Math.floor(Math.random() * pts.length)];
  return roundDose(raw);
};

const randWeight = (species) =>
  species === "cat"
    ? Math.round((2 + Math.random() * 4) * 10) / 10 // 2.0–6.0 kg
    : Math.round((4 + Math.random() * 30) * 10) / 10; // 4.0–34.0 kg

const letterGrade = (s) =>
  s >= 90 ? "A" : s >= 80 ? "B" : s >= 70 ? "C" : s >= 60 ? "D" : "F";

export default function DrugCalc() {
  const navigate = useNavigate();

  const [species, setSpecies] = useState("dog");
  const [weight, setWeight] = useState(12);
  // selected = [{ id, dose }]
  const [selected, setSelected] = useState([]);
  const [answers, setAnswers] = useState({}); // { id: "0.36" }
  const [result, setResult] = useState(null);
  const [showWorking, setShowWorking] = useState(false);
  const [calcValue, setCalcValue] = useState("0");

  const available = useMemo(
    () => DRUG_POOL.filter((d) => d.species.includes(species)),
    [species]
  );

  const clearResults = () => {
    setResult(null);
    setShowWorking(false);
  };

  const setSpeciesSafe = (s) => {
    if (s === species) return;
    setSpecies(s);
    // re-roll doses for drugs that still apply; drop unsupported
    setSelected((prev) =>
      prev
        .map((sel) => byId(sel.id))
        .filter((d) => d && d.species.includes(s))
        .map((d) => ({ id: d.id, dose: pickDose(d, s) }))
    );
    setAnswers({});
    clearResults();
  };

  const setWeightSafe = (val) => {
    setWeight(val);
    clearResults();
  };

  const toggleDrug = (drug) => {
    clearResults();
    setSelected((prev) => {
      const exists = prev.some((s) => s.id === drug.id);
      if (exists) {
        setAnswers((a) => {
          const next = { ...a };
          delete next[drug.id];
          return next;
        });
        return prev.filter((s) => s.id !== drug.id);
      }
      return [...prev, { id: drug.id, dose: pickDose(drug, species) }];
    });
  };

  const newPatient = () => {
    const sp = Math.random() > 0.5 ? "dog" : "cat";
    setSpecies(sp);
    setWeight(randWeight(sp));
    setSelected([]);
    setAnswers({});
    clearResults();
    toast.message("New patient generated — choose drugs and calculate the volumes");
  };

  const handleAnswer = (id, val) => {
    if (result) clearResults();
    setAnswers((a) => ({ ...a, [id]: val }));
  };

  const check = () => {
    if (selected.length === 0) return toast.error("Select at least one drug first");
    const w = parseFloat(weight);
    if (!w || w <= 0) return toast.error("Enter a valid patient weight");

    const perDrug = selected.map((sel) => {
      const drug = byId(sel.id);
      const correct = calcVolumeForDose(drug, w, sel.dose);
      const user = parseFloat(answers[sel.id]);
      const answered = !isNaN(user);
      const tol = Math.max(0.02, correct * 0.03); // ±3% or 0.02 mL
      const ok = answered && Math.abs(user - correct) <= tol;
      return { id: sel.id, name: drug.name, dose: sel.dose, drug, correct, user, ok, answered };
    });

    const correctCount = perDrug.filter((p) => p.ok).length;
    const score = Math.round((correctCount / perDrug.length) * 100);
    setResult({ score, grade: letterGrade(score), perDrug, correctCount });
    setShowWorking(true);

    if (score === 100) toast.success("Perfect — every volume is correct!");
    else if (score >= 60) toast.message(`${correctCount}/${perDrug.length} correct`);
    else toast.error(`${correctCount}/${perDrug.length} correct — review the working`);
  };

  const reset = () => {
    setSelected([]);
    setAnswers({});
    clearResults();
  };

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav
        current={1}
        steps={DRUGCALC_STEPS}
        title="Drug volume calculator"
        subtitle="Anaesthetic drug dose-to-volume challenge"
      />
      <main className="max-w-6xl mx-auto px-6 py-8 fade-up">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
            <Calculator className="w-3.5 h-3.5" />
            <span>Dose calculation</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Calculate the volume
          </h1>
          <p className="text-base text-slate-600 mt-2 max-w-2xl">
            Choose the drugs you want to administer, then work out the{" "}
            <span className="font-semibold text-slate-900">volume to draw up (mL)</span>{" "}
            from the ordered dose, the patient&apos;s weight and the drug concentration.
            No answers are shown until you check your working.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* ---------------- Left: scenario + drug picker ---------------- */}
          <div className="space-y-5">
            {/* Scenario */}
            <Card className="border-slate-200" data-testid="scenario-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                  <Syringe className="w-4 h-4 text-[#0047AB]" />
                  Patient
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
                    Species
                  </div>
                  <div className="flex gap-2">
                    {["dog", "cat"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSpeciesSafe(s)}
                        data-testid={`species-${s}-btn`}
                        className={`flex-1 text-sm font-semibold py-2 rounded-md border-2 capitalize transition-colors ${
                          species === s
                            ? "border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
                    Weight (kg)
                  </div>
                  <Input
                    type="number"
                    min="0.5"
                    step="0.1"
                    value={weight}
                    onChange={(e) => setWeightSafe(e.target.value)}
                    data-testid="weight-input"
                    className="font-mono-clinical"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={newPatient}
                  data-testid="new-patient-btn"
                  className="w-full text-slate-600"
                >
                  <Dices className="w-4 h-4 mr-1.5" />
                  New random patient
                </Button>
              </CardContent>
            </Card>

            {/* Drug picker */}
            <Card className="border-slate-200" data-testid="drug-picker-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                  Choose drugs
                  <span className="ml-2 text-xs font-mono text-[#0047AB]">
                    {selected.length} selected
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[420px] overflow-y-auto">
                {available.map((d) => {
                  const isSel = selected.some((s) => s.id === d.id);
                  return (
                    <div
                      key={d.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleDrug(d)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleDrug(d);
                        }
                      }}
                      className={`flex items-center gap-3 border-2 rounded-md px-3 py-2.5 cursor-pointer transition-colors ${
                        isSel
                          ? "border-[#0047AB] bg-[#0047AB]/5"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                      data-testid={`pick-toggle-${d.id}`}
                    >
                      <Checkbox
                        checked={isSel}
                        tabIndex={-1}
                        className="pointer-events-none"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {d.name}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {d.category}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button
                onClick={reset}
                variant="outline"
                data-testid="drugcalc-reset-btn"
                className="text-slate-600"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Clear
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/sections")}
                data-testid="back-to-home-btn"
                className="text-slate-600"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Simulator Hub
              </Button>
            </div>
          </div>

          {/* ---------------- Right: the prescription / worksheet ---------------- */}
          <div className="lg:col-span-2 space-y-5">
            <Card className="border-slate-200" data-testid="worksheet-card">
              <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                  Prescription · draw up the volumes
                </CardTitle>
                <button
                  type="button"
                  onClick={() => setShowWorking((s) => !s)}
                  data-testid="toggle-working-btn"
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#0047AB] hover:underline"
                >
                  {showWorking ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {showWorking ? "Hide" : "Show"} formula
                </button>
              </CardHeader>
              <CardContent className="space-y-3">
                {selected.length === 0 && (
                  <div
                    className="text-sm text-slate-500 text-center py-10 border-2 border-dashed border-slate-200 rounded-md"
                    data-testid="empty-state"
                  >
                    Select one or more drugs from the left to build the prescription.
                  </div>
                )}

                {showWorking && selected.length > 0 && (
                  <div className="flex items-start gap-2 text-xs text-[#0047AB] bg-[#0047AB]/5 border border-[#0047AB]/20 rounded-md px-3 py-2 font-mono-clinical">
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      Volume (mL) = (Dose × Weight) ÷ Concentration. Convert mcg → mg
                      (÷ 1000) when the dose is in mcg/kg.
                    </span>
                  </div>
                )}

                {selected.map((sel) => (
                  <WorksheetRow
                    key={sel.id}
                    sel={sel}
                    drug={byId(sel.id)}
                    species={species}
                    weight={weight}
                    result={result?.perDrug.find((p) => p.id === sel.id)}
                    answer={answers[sel.id] ?? ""}
                    onAnswer={handleAnswer}
                    showWorking={showWorking}
                    calcValue={calcValue}
                  />
                ))}

                {selected.length > 0 && (
                  <Button
                    onClick={check}
                    data-testid="check-answers-btn"
                    className="w-full bg-[#0047AB] hover:bg-[#003380] text-white"
                  >
                    Check my answers
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* simple traditional calculator */}
            <BasicCalculator onValueChange={setCalcValue} />

            {/* result */}
            {result && <ResultCard result={result} />}
            {result && (
              <SaveSession
                simulatorId="drugcalc"
                simulatorName="Drug Volume Calculator"
                score={result.score}
                grade={result.grade}
                summary={`${result.correctCount}/${result.perDrug.length} volumes correct`}
              />
            )}
            <FurtherReading topic="dosing" />
          </div>
        </div>
      </main>
    </div>
  );
}

const ResultCard = ({ result }) => {
  const color =
    result.score >= 80 ? "text-emerald-600" : result.score >= 60 ? "text-amber-600" : "text-red-600";
  return (
    <Card className="border-slate-200" data-testid="drugcalc-result">
      <CardContent className="pt-5 flex items-center gap-4">
        <div className={`text-5xl font-bold leading-none ${color}`} data-testid="drugcalc-grade">
          {result.grade}
        </div>
        <div>
          <div className="text-xl font-bold text-slate-900" data-testid="drugcalc-score">
            {result.score}/100
          </div>
          <div className="text-sm text-slate-500">
            {result.correctCount} of {result.perDrug.length} volumes correct
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
